<script setup>
import { ref } from 'vue';
import CloseIcon from '@assets/icons/close.svg';
import MenuIcon from '@assets/icons/menu.svg';
import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuTrigger,
} from 'reka-ui';

const props = defineProps({
    navigationItems: Array,
});

const activeDropdown = ref(null);

const triggerClass =
    'flex items-center gap-5 text-primary hover:bg-primary hover:text-primary-foreground active:bg-black active:text-white focusable py-3.5 sm:py-5 px-6 sm:px-15 text-md font-bold w-full text-left';
const itemClass =
    'flex items-center gap-5 text-primary hover:bg-primary hover:text-primary-foreground active:bg-black active:text-white focusable py-3.5 sm:py-5 px-6 sm:px-15 text-md font-normal';

const toggleDropdown = (index) => {
    activeDropdown.value = activeDropdown.value === index ? null : index;
};
</script>

<template>
    <DropdownMenuRoot :modal="true">
        <DropdownMenuTrigger
            class="group text-primary hover:bg-primary hover:text-primary-foreground flex size-24 items-center justify-center focus:outline-0 active:bg-black active:text-white data-[state=open]:shadow"
            aria-label="Hauptnavigation öffnen"
        >
            <div class="relative z-30 size-12 lg:size-20">
                <MenuIcon
                    class="absolute top-1/2 left-1/2 block h-6 w-6 -translate-1/2 fill-current group-data-[state=open]:hidden"
                />
                <CloseIcon
                    class="absolute top-1/2 left-1/2 hidden h-6 w-6 -translate-1/2 group-data-[state=open]:block"
                />
            </div>
        </DropdownMenuTrigger>

        <DropdownMenuPortal>
            <DropdownMenuContent
                class="bg-background text-foreground isolate z-50 flex w-90 flex-col rounded-bl border-none shadow"
                style="
                    max-height: var(
                        --reka-popper-available-height,
                        calc(100vh - 140px)
                    );
                "
                align="end"
                :avoid-collisions="false"
            >
                <!-- Navigation items -->
                <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                    <template
                        v-for="(item, index) in navigationItems"
                        :key="index"
                    >
                        <!-- Dropdown items with manual accordion -->
                        <div v-if="item.type === 'menuDropdown'">
                            <button
                                @click="toggleDropdown(index)"
                                :class="[
                                    triggerClass,
                                    index === navigationItems.length - 1 &&
                                    !activeDropdown
                                        ? 'hover:rounded-bl'
                                        : '',
                                ]"
                                :aria-expanded="activeDropdown === index"
                                :aria-controls="`dropdown-${index}`"
                            >
                                <span class="flex-1">{{ item.title }}</span>
                                <svg
                                    class="h-5 w-5 transition-transform"
                                    :class="{
                                        'rotate-180': activeDropdown === index,
                                    }"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </button>

                            <Transition
                                enter-active-class="transition-all duration-200 ease-out"
                                enter-from-class="max-h-0 opacity-0"
                                enter-to-class="max-h-96 opacity-100"
                                leave-active-class="transition-all duration-150 ease-in"
                                leave-from-class="max-h-96 opacity-100"
                                leave-to-class="max-h-0 opacity-0"
                            >
                                <div
                                    v-show="activeDropdown === index"
                                    :id="`dropdown-${index}`"
                                    class="overflow-hidden bg-white"
                                >
                                    <template
                                        v-for="(
                                            dropdownItem, dropdownIndex
                                        ) in item.dropdownItems"
                                        :key="dropdownIndex"
                                    >
                                        <a
                                            :href="dropdownItem.url || '#'"
                                            :class="itemClass"
                                            v-html="
                                                dropdownItem.text ||
                                                dropdownItem.title
                                            "
                                        />
                                    </template>
                                </div>
                            </Transition>
                        </div>

                        <!-- Simple menu items -->
                        <DropdownMenuItem
                            v-else-if="item.type === 'menuItem'"
                            as-child
                            :class="[
                                triggerClass,
                                index === navigationItems.length - 1
                                    ? 'hover:rounded-bl'
                                    : '',
                            ]"
                        >
                            <a
                                :href="item.url || '#'"
                                v-html="item.text || item.title"
                            />
                        </DropdownMenuItem>
                    </template>
                </div>
            </DropdownMenuContent>
        </DropdownMenuPortal>
    </DropdownMenuRoot>
</template>
